import { useCallback, useEffect, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useUser } from '../context/UserContext';

/**
 * Persists the per-view order of summary stat cards. The chosen order lives on
 * the user doc (`summaryCardOrder.{viewId}`) so it syncs across devices, with a
 * localStorage cache keyed by uid + viewId for instant paint before Firestore
 * resolves. Only the order of *movable* cards is stored — pinned cards are
 * always rendered first by the grid regardless of the stored order.
 */
export function useSummaryCardOrder(viewId: string): {
  order: string[] | undefined;
  setOrder: (ids: string[]) => void;
} {
  const { user, summaryCardOrder } = useUser();
  const uid = user?.uid;
  const cacheKey = uid ? `summaryCardOrder:${uid}:${viewId}` : null;

  const readCache = useCallback((): string[] | undefined => {
    if (!cacheKey) return undefined;
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.every((id) => typeof id === 'string')) {
        return parsed as string[];
      }
    } catch {
      /* ignore malformed cache */
    }
    return undefined;
  }, [cacheKey]);

  const [order, setOrderState] = useState<string[] | undefined>(() => readCache());

  // Firestore is the source of truth: mirror it into state + cache when it loads.
  const remoteOrder = summaryCardOrder?.[viewId];
  useEffect(() => {
    if (!remoteOrder) return;
    setOrderState(remoteOrder);
    if (cacheKey) {
      try {
        localStorage.setItem(cacheKey, JSON.stringify(remoteOrder));
      } catch {
        /* ignore quota errors */
      }
    }
  }, [remoteOrder, cacheKey]);

  const setOrder = useCallback(
    (ids: string[]) => {
      const previous = order;
      setOrderState(ids);
      if (cacheKey) {
        try {
          localStorage.setItem(cacheKey, JSON.stringify(ids));
        } catch {
          /* ignore quota errors */
        }
      }
      if (!uid) return;
      updateDoc(doc(db, 'users', uid), { [`summaryCardOrder.${viewId}`]: ids }).catch((error) => {
        console.error('Failed to persist summary card order:', error);
        setOrderState(previous);
        if (cacheKey) {
          try {
            if (previous) localStorage.setItem(cacheKey, JSON.stringify(previous));
            else localStorage.removeItem(cacheKey);
          } catch {
            /* ignore */
          }
        }
      });
    },
    [order, cacheKey, uid, viewId],
  );

  return { order, setOrder };
}
